import { useScrollable } from '@capturi/react-utils'
import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { css } from '@emotion/react'
import { Trans } from '@lingui/react/macro'
import type { WidgetState } from 'copilot/Copilot'
import { type FC, useEffect, useRef, useState } from 'react'

import { AwaitingScreen } from './components/AwaitingScreen'
import Background from './components/Background'
import { BottomBar } from './components/BottomBar'
import { LiveSummaryActionButton } from './LiveSummaryActionButton'
import { AskCopilotChat } from './modules/AskCopilot/AskCopilotChat'
import { AskCopilotError } from './modules/AskCopilot/AskCopilotError'
import { InputBox } from './modules/AskCopilot/InputBox'
import { useAskQuestion } from './modules/AskCopilot/useAskCopilot'
import { Handover } from './modules/Handover'
import { LiveSummary } from './modules/LiveSummary'
import { PreviousInteractions } from './modules/PreviousInteractions/PreviousInteractions'
import { VoiceBotHandover } from './modules/VoiceBotHandover'
import { useFeed } from './useFeed'

type Props = { state: WidgetState }

export const Widget: FC<Props> = ({ state }) => {
  const ref = useRef<HTMLDivElement>(null)
  const currentCallRef = useRef<HTMLDivElement>(null)
  const height = useScrollable(ref)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const { modules, currentCallState, config, data } = state
  const feed = useFeed(state.data.sessionUid)
  const { mutate: retryQuestion } = useAskQuestion({
    copilotToken: config.copilotApiToken,
    sessionUid: data.sessionUid,
  })

  const [showAwaiting, setShowAwaiting] = useState(true)
  const [previousCallState, setPreviousCallState] = useState(currentCallState)

  const answered =
    currentCallState === 'handling' || currentCallState === 'wrapUp'

  if (previousCallState !== currentCallState)
    setPreviousCallState(currentCallState)

  if (currentCallState === 'awaiting' && previousCallState !== 'awaiting')
    setShowAwaiting(true)

  useEffect(() => {
    if (currentCallState === 'handling') {
      currentCallRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [currentCallState])

  return (
    <Box
      height="100vh"
      width="100%"
      position="relative"
      maxHeight={height}
      overflow="hidden"
      ref={ref}
      css={css`
        interpolate-size: allow-keywords;
      `}
    >
      {showAwaiting &&
        (currentCallState === 'awaiting' ||
          currentCallState === 'alerting') && (
          <Box
            opacity={state.currentCallState === 'awaiting' ? '100%' : '0%'}
            borderRadius={
              state.currentCallState === 'awaiting' ? '0px' : '100px'
            }
            transition="all 700ms ease"
            width="100%"
            position="relative"
            maxHeight={height}
            overflow="hidden"
            onTransitionEnd={() => {
              if (currentCallState !== 'awaiting') setShowAwaiting(false)
            }}
          >
            <AwaitingScreen />
          </Box>
        )}
      <Background />
      <Flex
        flexDirection="column-reverse"
        overflow="auto"
        scrollBehavior="smooth"
        maxHeight={height}
        p="8"
        pb="178px"
        ref={scrollContainerRef}
        overscrollBehavior="contain"
      >
        <Flex
          alignItems="center"
          justifyContent="center"
          direction="column"
          gap={0}
        >
          {modules.previousInteractions.enabled &&
            currentCallState !== 'awaiting' && (
              <VStack spacing={6} mb={8} align="center">
                <PreviousInteractions
                  apiToken={config.copilotApiToken}
                  customer={data.customer}
                  showContent={config.waitUntilAnswer ? answered : true}
                />
                <Box
                  display="flex"
                  width="100%"
                  alignItems="center"
                  ref={currentCallRef}
                >
                  <Box height="1px" width="100%" bg="greys.300" />
                  <Text
                    whiteSpace="nowrap"
                    px="2"
                    fontSize="lg"
                    fontWeight="semibold"
                  >
                    <Trans>Current call</Trans>
                  </Text>
                  <Box height="1px" width="100%" bg="greys.300" />
                </Box>
              </VStack>
            )}
          {modules.handover.enabled && (
            <Handover
              copilotToken={config.copilotApiToken}
              customer={data.customer}
              showContent={modules.handover.show}
              containerRef={scrollContainerRef}
            />
          )}

          {modules.voiceBotHandover.enabled && (
            <VoiceBotHandover
              copilotToken={config.copilotApiToken}
              sessionUid={data.sessionUid}
              showContent={modules.voiceBotHandover.show}
              containerRef={scrollContainerRef}
            />
          )}

          {feed?.map((f, idx) => {
            if (f.type === 'summary' && modules.liveSummary.enabled) {
              return (
                <LiveSummary
                  idx={idx}
                  uid={f.uid}
                  summary={f.data.summary}
                  key={f.uid}
                  puzzelSessionUid={data.sessionUid}
                  apiToken={config.copilotApiToken}
                  queue={data.queue}
                  agentUserName={data.agentUserName}
                  lockSummaryEdit={modules.liveSummary.lockSummaryEdit}
                  getTranscription={data.getTranscription}
                />
              )
            }
            if (f.type === 'ask-copilot' && modules.askCopilot.enabled) {
              if (f.status === 'error') {
                return (
                  <AskCopilotError
                    key={f.uid}
                    question={f.data.question}
                    error={f.data.error}
                    retryConfig={{
                      feedItemUid: f.uid,
                      originalPayload: f.data.originalPayload,
                      isRetrying: false,
                      onRetry: retryQuestion,
                    }}
                  />
                )
              }

              return (
                <Box key={f.uid} width="100%">
                  <AskCopilotChat
                    data-testid={`feed-item-${idx}`}
                    chat={f.data.chat}
                    customer={data.customer}
                    containerRef={scrollContainerRef}
                  />
                </Box>
              )
            }

            return null
          })}
          <Box height="36px" />
        </Flex>
      </Flex>

      {modules.askCopilot.enabled && currentCallState !== 'awaiting' && (
        <InputBox state={state} />
      )}

      <BottomBar>
        {!modules.askCopilot.enabled && modules.liveSummary.enabled && (
          <LiveSummaryActionButton state={state} disabledReasonLayout="left" />
        )}
      </BottomBar>
    </Box>
  )
}
